/**
 *  @class
 *  @function NotifyMeForm
 *  Notify me (product-notify-me.liquid): sends the back-in-stock request
 *  with fetch, so quick view and a featured product keep their page, and
 *  shows the result inline. Shopify answers a contact or customer form with
 *  a redirect to the page it came from, with contact_posted=true or
 *  customer_posted=true (data-success-param) once it is saved, or with that
 *  page showing the form's errors. A request its spam protection holds back
 *  goes to its challenge page instead: the form is then posted the usual
 *  way, so the shopper completes it there, and comes back to the product's
 *  page (return_to), which shows the result. So is a request whose answer
 *  never arrives while the shopper is online. Without JavaScript the form
 *  posts natively.
 *
 *  Shopify's spam protection (hCaptcha), when the store has it on for
 *  contact forms, binds a contact or customer form as the shopper first
 *  focuses it, marking it data-cptcha="true": its own submit listener gets
 *  a token, then calls the form's submit method, which it keeps from before
 *  it bound the form. That method is send() here (connectedCallback), so
 *  the request still goes out with fetch, the token in it.
 */
if (!customElements.get('notify-me-form')) {
  // The email typed in each form, by form id: a variant change re-renders
  // the block (product.js), and picking another sold-out size keeps it.
  const drafts = {};

  // theme.strings (layout/theme.liquid), with English when they're missing.
  const notifyMeString = (name, fallback) => (window.theme && window.theme.strings && window.theme.strings[name]) || fallback;

  // The product a path shows: /products/<handle>, after a locale or a
  // /collections/<handle> or not.
  const productHandle = (path) => (path.match(/\/products\/([^/?#]+)/) || [])[1];

  class NotifyMeForm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.fields = this.querySelector('.product-notify-me__fields');
      this.email = this.form.querySelector('input[type="email"]');
      this.button = this.form.querySelector('button[type="submit"]');
      this.status = this.querySelector('[role="status"]');
      this.alert = this.querySelector('[role="alert"]');
      this.templates = this.querySelector('template');

      this.resetOtherPostedState();
      if (this.email && !this.email.value && drafts[this.form.id]) {
        this.email.value = drafts[this.form.id];
      }

      this.form.submit = () => this.send();
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.email) {
        this.email.addEventListener('input', () => {
          drafts[this.form.id] = this.email.value;
        });
      }
    }

    // A form posted the usual way comes back to a page where every form of
    // its kind reads as posted (form.posted_successfully?, form.errors).
    // When another form there was the one posted (its id is the URL's
    // hash), this one goes back to waiting for an email.
    resetOtherPostedState() {
      if (!this.querySelector('[data-posted], [data-errors]')) return;
      let hash = '';
      try {
        hash = decodeURIComponent(window.location.hash.slice(1));
      } catch (e) {
        return;
      }
      if (!hash || hash === this.form.id || !document.getElementById(hash)) return;
      this.status.replaceChildren();
      this.alert.replaceChildren();
      this.setInvalid(false);
      if (this.fields) this.fields.hidden = false;
    }

    onSubmit(event) {
      event.preventDefault();
      // Bound by the spam protection: its listener sends it.
      if (this.captchaBound()) return;
      // Being bound (its script loads as the form first gets focus): sent
      // through it once it is. A form it doesn't protect, as when the store
      // has it on for account forms only, is never marked and goes at once.
      if (this.form.dataset.cptcha === 'true') {
        this.sendOnceBound();
        return;
      }
      this.send();
    }

    captchaBound() {
      return this.form.hasAttribute('data-hcaptcha-bound') || this.form.hasAttribute('data-recaptcha-bound');
    }

    // Waits for the spam protection to bind the form, then submits through
    // it (a token, then send()). After a few seconds without it, sends the
    // form as it is: Shopify's challenge page takes over if it must.
    sendOnceBound() {
      if (this.waitingForCaptcha) return;
      this.waitingForCaptcha = true;
      this.setLoading(true);
      const finish = (bound) => {
        observer.disconnect();
        clearTimeout(timer);
        this.waitingForCaptcha = false;
        this.setLoading(false);
        if (bound) {
          this.form.submit();
        } else {
          this.send();
        }
      };
      const observer = new MutationObserver(() => {
        if (this.captchaBound()) finish(true);
      });
      const timer = setTimeout(() => finish(false), 3000);
      observer.observe(this.form, { attributes: true, attributeFilter: ['data-hcaptcha-bound', 'data-recaptcha-bound'] });
    }

    send() {
      if (this.button.classList.contains('loading')) return;
      this.setLoading(true);
      this.alert.replaceChildren();
      this.setInvalid(false);

      fetch(this.form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(this.form)),
        headers: { 'Accept': 'text/html' }
      })
        .then((response) => this.onResponse(response))
        .catch((error) => {
          console.error(error);
          // Offline: say so here. Otherwise the answer couldn't be read (a
          // redirect fetch can't follow), so the browser posts the form.
          if (navigator.onLine === false) {
            this.showError();
          } else {
            this.submitNatively();
          }
        })
        .finally(() => {
          if (!this.navigating) this.setLoading(false);
        });
    }

    onResponse(response) {
      const url = new URL(response.url || this.form.action, window.location.href);
      if (/\/challenge\/?$/.test(url.pathname)) {
        this.submitNatively();
        return;
      }
      if (url.searchParams.get(this.dataset.successParam) === 'true') {
        this.showSuccess();
        return;
      }
      if (!response.ok) {
        this.showError();
        return;
      }
      return response.text().then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        if (html.querySelector('.shopify-challenge__container, .h-captcha, .g-recaptcha')) {
          this.submitNatively();
          return;
        }
        // Not saved. The page it came back to renders this block's errors
        // when it shows the same product and variant (the product page);
        // any other page (quick view's, a featured product's) gets the
        // general message, without posting the request again.
        const errors = html.getElementById(this.alert.id);
        const message = errors ? errors.textContent.replace(/\s+/g, ' ').trim() : '';
        this.showError(message);
        if (message) this.setInvalid(true);
      });
    }

    // Posts the form the usual way: no submit listener runs for it, the
    // spam protection's included, and the token it set goes with it. Away
    // from the product's page (quick view, a featured product), Shopify
    // sends the shopper on to it afterwards, where this block shows the
    // result, rather than back to a page without it.
    submitNatively() {
      this.navigating = true;
      const returnTo = this.dataset.returnTo;
      if (returnTo && productHandle(new URL(returnTo, window.location.href).pathname) !== productHandle(window.location.pathname) && !this.form.elements.return_to) {
        this.form.append(Object.assign(document.createElement('input'), { type: 'hidden', name: 'return_to', value: returnTo }));
      }
      HTMLFormElement.prototype.submit.call(this.form);
    }

    showSuccess() {
      delete drafts[this.form.id];
      const hadFocus = this.contains(document.activeElement) || document.activeElement === document.body;
      this.showMessage(this.status, 'success', notifyMeString('notifyMeSuccess', 'Thanks! We\'ll email you when it\'s back in stock.'));
      if (this.fields) this.fields.hidden = true;
      // The button that had focus is gone: the message takes it.
      if (hadFocus) this.status.focus({ preventScroll: true });
    }

    showError(message) {
      this.showMessage(this.alert, 'error', message || notifyMeString('notifyMeError', 'We couldn\'t save your request. Please try again.'));
    }

    // A message in a live region, from the block's template (its icon).
    showMessage(region, type, text) {
      const template = this.templates && this.templates.content.querySelector(`.form-notification.${type}`);
      const message = template ? template.cloneNode(true) : document.createElement('div');
      (message.querySelector('span') || message).textContent = text;
      region.replaceChildren(message);
    }

    // The email field, marked as what the server turned down.
    setInvalid(invalid) {
      if (!this.email) return;
      if (invalid) {
        this.email.setAttribute('aria-invalid', 'true');
        this.email.setAttribute('aria-describedby', this.alert.id);
      } else {
        this.email.removeAttribute('aria-invalid');
        this.email.removeAttribute('aria-describedby');
      }
    }

    setLoading(loading) {
      this.button.classList.toggle('loading', loading);
      if (loading) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('notify-me-form', NotifyMeForm);
}
